import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Clock, CheckCircle2, AlertTriangle, Ban, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { firmasAPI } from '../api';
import CompartirEnlaceFirma from './CompartirEnlaceFirma';
import { toast } from '../hooks/use-toast';

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '');

// Estado de las solicitudes de firma de un expediente (una sola llamada, sin polling).
export const useFirmasExpediente = (expedienteId) => {
  const [firmas, setFirmas] = useState([]);

  const reload = useCallback(async () => {
    if (!expedienteId) return;
    try {
      const res = await firmasAPI.getByExpediente(expedienteId);
      setFirmas(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setFirmas([]);
    }
  }, [expedienteId]);

  useEffect(() => { reload(); }, [reload]);

  return useMemo(() => {
    const porAdjunto = {};
    const porActuacion = {};
    firmas.forEach((f) => {
      [f.adjunto_id, f.adjunto_firmado_id].forEach((aid) => {
        if (aid && !porAdjunto[aid]) porAdjunto[aid] = f;
      });
      if (f.actuacion_id) {
        porActuacion[f.actuacion_id] = [...(porActuacion[f.actuacion_id] || []), f];
      }
    });
    return {
      firmas,
      porAdjunto,
      porActuacion,
      pendientes: firmas.filter((f) => f.estado === 'pendiente'),
      caducadas: firmas.filter((f) => f.estado === 'caducada'),
      reload,
    };
  }, [firmas, reload]);
};

// Etiqueta del estado de firma de un documento. Las caducadas permiten renovar el enlace.
export const FirmaBadge = ({ firma, onRenovado }) => {
  const [renovando, setRenovando] = useState(false);
  if (!firma) return null;

  const renovar = async (e) => {
    e.stopPropagation();
    setRenovando(true);
    try {
      const res = await firmasAPI.renovar(firma.id, { enviar_email: true });
      toast({
        title: res.data?.email_enviado ? 'Enlace reenviado al cliente' : 'Enlace nuevo creado',
        description: res.data?.email_enviado
          ? `Enviado a ${res.data?.email_enviado_a || 'el cliente'}`
          : 'No se pudo enviar el email: copia el enlace desde el diálogo de firma',
        variant: res.data?.email_enviado === false ? 'destructive' : 'default',
      });
      if (onRenovado) onRenovado();
    } catch (err) {
      toast({
        title: 'No se pudo renovar',
        description: err?.response?.data?.detail || 'Error inesperado',
        variant: 'destructive',
      });
    } finally {
      setRenovando(false);
    }
  };

  const base = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium flex-shrink-0';

  if (firma.estado === 'firmado') {
    return (
      <span className={`${base} bg-green-100 text-green-800`}
            title={`Firmado por ${firma.firmante_nombre || 'el cliente'}`}
            data-testid={`firma-badge-${firma.id}`}>
        <CheckCircle2 className="w-3 h-3" /> Firmado {fecha(firma.firmado_at)}
      </span>
    );
  }
  if (firma.estado === 'pendiente') {
    const dias = firma.dias_esperando;
    return (
      <span className="inline-flex items-center gap-1 flex-shrink-0">
        <span className={`${base} bg-amber-100 text-amber-800`}
              title={`Enviado a ${firma.email_enviado_a || 'el cliente'} · caduca el ${fecha(firma.expira_en)}`
                     + (firma.recordatorios ? ` · ${firma.recordatorios} recordatorio(s)` : '')}
              data-testid={`firma-badge-${firma.id}`}>
          <Clock className="w-3 h-3" />
          Pendiente de firma{dias ? ` · ${dias} d` : ''}
        </span>
        <CompartirEnlaceFirma firma={firma} compacto />
      </span>
    );
  }
  if (firma.estado === 'caducada') {
    return (
      <span className="inline-flex items-center gap-1 flex-shrink-0">
        <span className={`${base} bg-red-100 text-red-700`}
              title={`El enlace enviado a ${firma.email_enviado_a || 'el cliente'} caducó el ${fecha(firma.expira_en)}`}
              data-testid={`firma-badge-${firma.id}`}>
          <AlertTriangle className="w-3 h-3" /> Caducada
        </span>
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]"
                disabled={renovando} onClick={renovar}
                title="Generar un enlace nuevo y reenviarlo al cliente"
                data-testid={`firma-renovar-btn-${firma.id}`}>
          <RefreshCw className={`w-3 h-3 mr-1 text-indigo-600 ${renovando ? 'animate-spin' : ''}`} />
          Renovar
        </Button>
      </span>
    );
  }
  return (
    <span className={`${base} bg-gray-100 text-gray-600`} data-testid={`firma-badge-${firma.id}`}>
      <Ban className="w-3 h-3" /> Firma anulada
    </span>
  );
};
